import api from '../../../services/api';

/**
 * DEVELOPMENT ONLY. Answers the panel's reads from fixtures so the whole of /admin can be
 * looked at — charts, tooltips, drills, the person drawer — without a staff key and
 * without touching a real API. Turned on by `/admin-preview/<section>?data=demo`;
 * `AdminPreview` is the only importer and App.js only registers that route when
 * NODE_ENV === 'development', so none of this reaches a production bundle.
 *
 * The shapes mirror the server exactly, including the wrapper: `api/admin/*` answers a
 * raw object and `api/analytics/*` answers { success, data }. Anything not listed here
 * falls through to the real client, which is what makes a missing fixture obvious.
 */

const DAY = 86_400_000;
const iso = (back) => new Date(Date.now() - back * DAY).toISOString().slice(0, 10);
const stamp = (minsBack) => new Date(Date.now() - minsBack * 60_000).toISOString();
const money = (cents) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PEOPLE = [
  { userId: '11111111-1111-1111-1111-111111111111', name: 'Maya Okonkwo', handle: 'maya', email: 'maya@example.com', city: 'Kelowna, BC' },
  { userId: '22222222-2222-2222-2222-222222222222', name: 'Sam Ellis', handle: 'sam', email: 'sam@example.com', city: 'Penticton, BC' },
  { userId: '33333333-3333-3333-3333-333333333333', name: 'Dana Reyes', handle: 'dana', email: 'dana@example.com', city: 'Vancouver, BC' },
  { userId: '44444444-4444-4444-4444-444444444444', name: 'Marcus Tan', handle: 'marcus', email: 'marcus@example.com', city: 'Kelowna, BC' },
  { userId: '55555555-5555-5555-5555-555555555555', name: 'Priya Nair', handle: 'priya', email: 'priya@example.com', city: 'Calgary, AB' },
];

const CITIES = ['KELOWNA', 'VANCOUVER', 'PENTICTON', 'CALGARY', 'VERNON'];
const REFERRERS = ['INSTAGRAM', 'DIRECT', 'GOOGLE', 'INVITE CARD', 'TIKTOK'];
const AREAS = ['MESSAGES', 'EVENT PAGES', 'EVENTS WALL', 'PORTAL PAGES', 'CITY PORTAL'];

/** A stable pseudo-random so a reload shows the same chart. */
const noise = (i, salt = 1) => Math.abs(Math.sin((i + 1) * 12.9898 * salt) * 43_758.5453) % 1;

const series = (days, build) => Array.from({ length: days }, (_, i) => build(days - 1 - i, i));

const counts = (labels, total, i) => labels.map((label, n) => ({
  label, count: Math.max(1, Math.round(total * (0.4 / (n + 1)) * (0.7 + noise(i, n + 2)))),
}));
const items = (labels, total, i, asMoney) => labels.map((label, n) => {
  const value = Math.max(1, Math.round(total * (0.4 / (n + 1)) * (0.7 + noise(i, n + 2))));
  return { label, value, display: asMoney ? money(value) : value.toLocaleString() };
});

const STATS = {
  totalUsers: 4213, totalPsInCirculation: 1_284_300, totalEscrow: 457_900, totalWithdrawalsCompleted: 2_410_000,
  totalWithdrawalCount: 318, totalFeesCollected: 212_450, totalEvents: 612, totalApplications: 8431,
  totalTransactions: 29_118, usersLast7Days: 63, eventsLast7Days: 11, transactionsLast7Days: 742,
  totalGuestCheckoutRevenue: 9_812_600, projectedEscrowFees: 32_053, partyStarterFeePercent: 7,
  eventsByStatus: { draft: 21, funding: 14, selling: 38, processing_greenlight: 1, greenlit: 9, ended: 472, dissolved: 7, canceled: 50 },
};

const BREAKDOWNS = {
  tiles: {
    balances: { key: 'balances', breakdownLabel: 'BY CITY', desc: 'What the platform owes people who have not cashed out.', source: 'wallets JOIN profiles GROUP BY profiles.location', items: items(CITIES, 1_284_300, 1, true) },
    escrow: { key: 'escrow', breakdownLabel: 'BY EVENT', desc: 'Guest money held until the event ends and the hold window passes.', source: 'applications WHERE committed > 0 AND NOT refunded AND the event has not released', items: [
      { label: 'ROOFTOP SUPPER CLUB', value: 184_000, display: '$1,840.00', href: '/admin/events/aaaa1111-0000-0000-0000-000000000001' },
      { label: 'VINYL + VERMOUTH', value: 121_500, display: '$1,215.00', href: '/admin/events/aaaa1111-0000-0000-0000-000000000002' },
      { label: 'LAKESIDE LONG TABLE', value: 96_400, display: '$964.00', href: '/admin/events/aaaa1111-0000-0000-0000-000000000003' },
    ] },
    revenue: { key: 'revenue', breakdownLabel: 'BY CITY', desc: 'Every ticket and admission ever sold, by where the party was.', source: 'wallet_transactions WHERE type IN (GuestCheckoutEarning, HostEventEarning, GatheringEscrowCredit) JOIN events GROUP BY events.location_city', items: items(CITIES, 9_812_600, 3, true) },
    fees: { key: 'fees', breakdownLabel: 'BY SOURCE', desc: 'Everything Portal has kept, split by which fee it was.', source: 'wallet_transactions fee types + withdrawals.fee_cents + pro_bookings fees', items: [
      { label: 'PORTAL SERVICE FEE', value: 148_300, display: '$1,483.00' },
      { label: 'PARTY STARTER FEE', value: 51_900, display: '$519.00' },
      { label: 'PRO BOOKING FEE', value: 7_400, display: '$74.00' },
      { label: 'GST ON THE SERVICE FEE', value: 7_415, display: '$74.15' },
      { label: 'PAYOUT FEE (AT COST)', value: 7_435, display: '$74.35' },
    ] },
    projectedFees: { key: 'projectedFees', breakdownLabel: 'BY EVENT', desc: 'What the 7% Party Starter fee would be if everything still held were greenlit today.', source: '7% of each event\'s held escrow', items: [
      { label: 'ROOFTOP SUPPER CLUB', value: 12_880, display: '$128.80', href: '/admin/events/aaaa1111-0000-0000-0000-000000000001' },
      { label: 'VINYL + VERMOUTH', value: 8_505, display: '$85.05', href: '/admin/events/aaaa1111-0000-0000-0000-000000000002' },
    ] },
    withdrawn: { key: 'withdrawn', breakdownLabel: 'BY PERSON', desc: 'Money that has actually left the platform, by who took it.', source: "withdrawals WHERE status = 'completed' GROUP BY user", items: items(PEOPLE.map((p) => p.name.toUpperCase()), 2_410_000, 5, true) },
    users: { key: 'users', breakdownLabel: 'BY CITY', desc: 'Where people said they are, on their own Portal Page.', source: 'profiles GROUP BY location', items: items(CITIES, 4213, 7) },
    events: { key: 'events', breakdownLabel: 'BY CITY', desc: 'Every event ever made, by where it was held.', source: 'events GROUP BY location_city', items: items(CITIES, 612, 9) },
    applications: { key: 'applications', breakdownLabel: 'BY STATUS', desc: 'Every seat ever asked for, and where the asking ended up.', source: 'applications GROUP BY status', items: items(['PAID', 'ACCEPTED', 'PENDING', 'DECLINED', 'WITHDRAWN'], 8431, 11) },
    transactions: { key: 'transactions', breakdownLabel: 'BY TYPE', desc: 'Every ledger line ever written, by what moved the money.', source: 'wallet_transactions GROUP BY type', items: items(['COMMITMENT', 'GUESTCHECKOUTEARNING', 'PRSERVICEFEE', 'TOPUP', 'WITHDRAWAL'], 29_118, 13) },
  },
};

const revenue = (days) => ({
  days,
  totalCents: 0,
  source: 'wallet_transactions WHERE type IN (GuestCheckoutEarning, HostEventEarning, GatheringEscrowCredit) GROUP BY day',
  series: series(days, (back, i) => {
    const cents = Math.round(12_000 + noise(i) * 46_000 + (i % 7 === 5 ? 38_000 : 0));
    return {
      date: iso(back),
      cents,
      count: 3 + Math.round(noise(i, 3) * 12),
      byCity: items(CITIES, cents, i, true),
      events: [
        { label: 'ROOFTOP SUPPER CLUB', value: Math.round(cents * 0.5), display: money(Math.round(cents * 0.5)), href: '/admin/events/aaaa1111-0000-0000-0000-000000000001' },
        { label: 'VINYL + VERMOUTH', value: Math.round(cents * 0.3), display: money(Math.round(cents * 0.3)), href: '/admin/events/aaaa1111-0000-0000-0000-000000000002' },
        { label: 'LAKESIDE LONG TABLE', value: Math.round(cents * 0.2), display: money(Math.round(cents * 0.2)), href: '/admin/events/aaaa1111-0000-0000-0000-000000000003' },
      ],
    };
  }),
});

const signups = (days) => ({
  days,
  total: 0,
  source: 'users WHERE created_at >= start GROUP BY day; arrival from referrals, else the tracker\'s first touch',
  series: series(days, (back, i) => {
    const count = 2 + Math.round(noise(i, 5) * 14);
    return {
      date: iso(back),
      count,
      byReferrer: items(REFERRERS, count, i),
      people: PEOPLE.slice(0, Math.min(PEOPLE.length, Math.max(1, Math.round(count / 3)))).map((p, n) => ({
        label: p.name.toUpperCase(), value: 1, display: REFERRERS[n % REFERRERS.length], href: `/admin/users?person=${p.userId}`,
      })),
    };
  }),
});

const ESCROW = {
  totalCents: 457_900,
  source: 'applications WHERE committed > 0 AND NOT refunded AND status IN (paid, accepted) AND the event has not released',
  events: [
    { eventId: 'aaaa1111-0000-0000-0000-000000000001', title: 'Rooftop Supper Club', status: 'selling', hostName: 'Maya Okonkwo', city: 'Kelowna, BC', dateTime: new Date(Date.now() + 9 * DAY).toISOString(), cents: 184_000, guests: 22, contributors: items(PEOPLE.map((p) => p.name.toUpperCase()), 184_000, 2, true) },
    { eventId: 'aaaa1111-0000-0000-0000-000000000002', title: 'Vinyl + Vermouth', status: 'funding', hostName: 'Sam Ellis', city: 'Penticton, BC', dateTime: new Date(Date.now() + 16 * DAY).toISOString(), cents: 121_500, guests: 14, contributors: items(PEOPLE.map((p) => p.name.toUpperCase()), 121_500, 4, true) },
    { eventId: 'aaaa1111-0000-0000-0000-000000000003', title: 'Lakeside Long Table', status: 'greenlit', hostName: 'Dana Reyes', city: 'Vernon, BC', dateTime: new Date(Date.now() + 4 * DAY).toISOString(), cents: 96_400, guests: 18, contributors: items(PEOPLE.map((p) => p.name.toUpperCase()), 96_400, 6, true) },
    { eventId: 'aaaa1111-0000-0000-0000-000000000004', title: 'Night Market Crawl', status: 'selling', hostName: 'Priya Nair', city: 'Calgary, AB', dateTime: new Date(Date.now() + 21 * DAY).toISOString(), cents: 56_000, guests: 9, contributors: items(PEOPLE.map((p) => p.name.toUpperCase()), 56_000, 8, true) },
  ],
};

const FEES = {
  days: 0,
  totalCents: 222_450,
  sources: [
    { label: 'PORTAL SERVICE FEE', cents: 148_300, count: 1483, source: "wallet_transactions WHERE type = 'PRServiceFee'", desc: 'The $1 a guest pays on every checkout.' },
    { label: 'PARTY STARTER FEE', cents: 51_900, count: 74, source: "wallet_transactions WHERE type = 'PartyStarterFee'", desc: 'A crowdfunded event\'s 7%, taken at greenlight.' },
    { label: 'GST ON THE SERVICE FEE', cents: 7_415, count: 1483, source: "wallet_transactions WHERE type = 'PRServiceFeeGST'", desc: '5% on the service fee, held for the CRA.' },
    { label: 'PAYOUT FEE (AT COST)', cents: 7_435, count: 318, source: "withdrawals WHERE status = 'completed' SUM(fee_cents)", desc: 'Stripe\'s own payout charge, passed through — Portal keeps none of it.' },
    { label: 'PRO BOOKING FEE', cents: 7_400, count: 18, source: 'pro_bookings SUM(buyer_fee_cents + provider_fee_cents)', desc: 'Both sides of a Pro booking\'s platform fee.' },
  ],
};

const overview = (days) => ({
  days,
  excludedPeople: 4,
  totals: { events: 184_210, uniqueVisitors: 6_412, signedInUsers: 1_208, sessions: 11_904, signedInSessions: 4_118, pageViews: 48_320, pagesPerSession: 4.1, avgEngagementSec: 182, totalEngagementSec: 2_166_528 },
  series: series(days, (back, i) => {
    const sessions = 180 + Math.round(noise(i, 2) * 320);
    const pageViews = sessions * (3 + Math.round(noise(i, 4) * 3));
    const engagementSec = sessions * (120 + Math.round(noise(i, 6) * 160));
    return {
      date: iso(back), visitors: Math.round(sessions * 0.78), pageViews, sessions,
      signedIn: Math.round(sessions * 0.34), engagementSec,
      byReferrer: counts(REFERRERS, sessions, i),
      byArea: counts(AREAS, engagementSec, i),
    };
  }),
  topPages: [
    { label: '/featured', count: 14_208 }, { label: '/square/kelowna', count: 6_410 },
    { label: '/messages', count: 5_902 }, { label: '/e/kelowna/rooftop-supper-club', count: 3_118 },
    { label: '/maya', count: 2_044 },
  ],
  topCities: [
    { city: 'Kelowna', count: 18_402, lat: 49.888, lng: -119.496 },
    { city: 'Vancouver', count: 11_209, lat: 49.283, lng: -123.121 },
    { city: 'Penticton', count: 4_118, lat: 49.5, lng: -119.594 },
    { city: 'Calgary', count: 3_902, lat: 51.045, lng: -114.057 },
  ],
  devices: [{ label: 'mobile', count: 31_208 }, { label: 'desktop', count: 14_902 }, { label: 'tablet', count: 2_210 }],
  topEvents: [{ label: 'page_view', count: 48_320 }, { label: 'engagement', count: 41_118 }, { label: 'checkout_paid', count: 412 }],
  funnel: { signalsCreated: 1_204, applicationsSent: 812, checkoutsPaid: 412 },
  topReferrers: counts(REFERRERS, 11_904, 1),
  timeByArea: counts(AREAS, 2_166_528, 1),
});

const people = (days) => ({
  days,
  excludedPeople: 4,
  peopleInWindow: 1_208,
  onlineNow: PEOPLE.slice(0, 3).map((p, i) => ({
    ...p, avatarUrl: null, isStaff: i === 2, isTest: false,
    path: ['/featured', '/messages', '/admin/stats'][i], device: ['mobile', 'desktop', 'desktop'][i],
    lastSeen: stamp(i), sessionSec: 240 + i * 180,
  })),
  topUsers: PEOPLE.map((p, i) => ({
    ...p, avatarUrl: null, isStaff: i === 2, isTest: false,
    visits: 42 - i * 6, pageViews: 310 - i * 40, timeSec: 18_400 - i * 2_600,
    firstSeen: stamp(60 * 24 * 20), lastSeen: stamp(i * 37),
    topPath: ['/featured', '/messages', '/admin/stats', '/square/kelowna', '/maya'][i],
    device: i % 2 === 0 ? 'mobile' : 'desktop',
    dailyVisits: Array.from({ length: days }, (_, d) => Math.round(noise(d, i + 2) * 6)),
  })),
});

const day = (date) => ({
  date,
  visitors: 412, sessions: 508, pageViews: 2_110, engagementSec: 92_400, anonymousVisitors: 188,
  referrers: counts(REFERRERS, 508, 3),
  areas: counts(AREAS, 92_400, 3),
  pages: [{ label: '/featured', count: 610 }, { label: '/messages', count: 408 }, { label: '/square/kelowna', count: 302 }],
  people: PEOPLE.map((p, i) => ({ ...p, avatarUrl: null, visits: 5 - i, pageViews: 40 - i * 6, timeSec: 2_400 - i * 300, topPath: ['/featured', '/messages', '/admin/stats', '/square/kelowna', '/maya'][i] })),
});

const person = (userId, days) => {
  const p = PEOPLE.find((x) => x.userId === userId) || PEOPLE[0];
  const who = PEOPLE.indexOf(p);
  const daily = series(days, (back, i) => {
    const visits = Math.round(noise(i, 9) * 5);
    return {
      date: iso(back), visits, pageViews: visits * 4, timeSec: visits * 420,
      did: counts(AREAS.slice(0, 3).concat(['CHECKOUT PAID']), Math.max(1, visits * 4), i),
    };
  });
  return {
    days,
    person: {
      ...p, avatarUrl: null, joinedAt: new Date(Date.now() - 240 * DAY).toISOString(),
      identityVerified: true, isStaff: p.handle === 'dana', isSuperadmin: false, isTest: false,
      staffRole: p.handle === 'dana' ? 'MARKETING' : null, isOnline: p.handle === 'maya',
      lastSeen: stamp(3), friendCount: 48, clubCount: 4, portalPath: `/${p.handle}`,
      prServiceFeeOverrideCents: null,
    },
    stats: {
      visits: daily.reduce((n, d) => n + d.visits, 0),
      pageViews: daily.reduce((n, d) => n + d.pageViews, 0),
      timeSec: daily.reduce((n, d) => n + d.timeSec, 0),
      pagesPerVisit: 4.0, device: 'mobile', hostedCount: 3, attendedCount: 11,
      balanceCents: 4_250, escrowCents: 12_000, spentCents: 48_900, earnedCents: 121_400,
      noTracking: false,
    },
    daily,
    areas: counts(AREAS, 41_000, 2),
    referrers: counts(REFERRERS, daily.reduce((n, d) => n + d.visits, 0), 4),
    // Two shapes worth being able to see: an invited account with a campaign on it, and
    // one we know nothing about, which is what every pre-Oct-2026 account looks like.
    origin: who === 0
      ? {
        signupMethod: 'google', landingPath: '/i/7h3k9m2p', referrer: 'instagram.com', referrerLabel: 'INSTAGRAM',
        utmSource: 'instagram', utmMedium: 'bio', utmCampaign: 'summer-rooftops',
        inviteCode: '7h3k9m2p', invitedByUserId: PEOPLE[1].userId, invitedByName: PEOPLE[1].name.toUpperCase(),
        invitedByHref: `/admin/users?person=${PEOPLE[1].userId}`, invitedAt: new Date(Date.now() - 40 * DAY).toISOString(),
      }
      : who === 1
        ? { signupMethod: 'email', landingPath: '/featured', referrer: '', referrerLabel: 'DIRECT' }
        : {},
    topPages: [{ label: '/featured', count: 88 }, { label: '/messages', count: 61 }, { label: `/${p.handle}`, count: 24 }],
    events: [
      { eventId: 'aaaa1111-0000-0000-0000-000000000001', title: 'Rooftop Supper Club', status: 'selling', dateTime: new Date(Date.now() + 9 * DAY).toISOString(), role: 'hosted', committedCents: 0 },
      { eventId: 'aaaa1111-0000-0000-0000-000000000003', title: 'Lakeside Long Table', status: 'greenlit', dateTime: new Date(Date.now() + 4 * DAY).toISOString(), role: 'guest', committedCents: 6_500 },
    ],
    activity: [
      { at: stamp(12), label: 'GuestCheckoutEarning', detail: '+$65.00 · Lakeside Long Table', kind: 'money' },
      { at: stamp(140), label: 'Asked for a seat', detail: 'Lakeside Long Table', kind: 'event' },
      { at: stamp(1_400), label: 'checkout paid', detail: '/e/kelowna/lakeside-long-table', kind: 'visit' },
      { at: stamp(60 * 24 * 240), label: 'Made an account', detail: p.email, kind: 'account' },
    ],
  };
};

/** One person on one day: two visits, in order, with the steps inside them. */
const PATHS = ['/featured', '/e/kelowna/rooftop-supper-club', '/messages', '/maya', '/square/kelowna'];
const personDay = (userId, date) => {
  const p = PEOPLE.find((x) => x.userId === userId) || PEOPLE[0];
  const base = new Date(`${date}T17:40:00Z`).getTime();
  const visit = (n, steps) => {
    const start = base + n * 3 * 3_600_000;
    return {
      startedAt: new Date(start).toISOString(),
      endedAt: new Date(start + steps * 4 * 60_000).toISOString(),
      timeSec: steps * 190,
      referrer: REFERRERS[n % REFERRERS.length],
      device: n === 0 ? 'mobile' : 'desktop',
      city: p.city.split(',')[0],
      pageViews: steps,
      moreSteps: 0,
      steps: Array.from({ length: steps }, (_, i) => (i === steps - 1 ? {
        at: new Date(start + i * 4 * 60_000).toISOString(), kind: 'event',
        label: 'CHECKOUT PAID', detail: PATHS[1], secondsHere: 0,
      } : {
        at: new Date(start + i * 4 * 60_000).toISOString(), kind: 'page',
        label: PATHS[(i + n) % PATHS.length], detail: AREAS[(i + n) % AREAS.length],
        secondsHere: Math.round(60 + noise(i, n + 3) * 220),
      })),
    };
  };
  const sessions = [visit(0, 5), visit(1, 3)];
  return {
    userId, name: p.name, date,
    visits: sessions.length,
    pageViews: sessions.reduce((n, v) => n + v.pageViews, 0),
    timeSec: sessions.reduce((n, v) => n + v.timeSec, 0),
    referrers: sessions.map((v) => ({ label: v.referrer, count: 1 })),
    did: counts(AREAS.slice(0, 3), 8, 2),
    sessions,
  };
};

const USERS = {
  total: 4213,
  users: PEOPLE.map((p, i) => ({
    id: p.userId, email: p.email, displayName: p.name, role: i === 2 ? 'admin' : null,
    identityVerified: i !== 4, walletBalance: 4_250 - i * 400, walletEscrow: i === 0 ? 92_000 : i === 2 ? 11_399 : 0,
    prServiceFeeOverrideCents: i === 1 ? 0 : null,
    isTestAccount: false,
  })),
};

/** The USERS record (`GET admin/users/{id}`): the wallet strip and the five tabs. */
const userDetail = (userId) => {
  const p = PEOPLE.find((x) => x.userId === userId) || PEOPLE[0];
  const i = PEOPLE.indexOf(p);
  const titles = ['Rooftop Sunset Social', 'Comedy for a Cause', 'Wine & Trivia Night', 'Studio Sessions'];
  return {
    user: { id: p.userId, email: p.email, role: i === 2 ? 'admin' : null, identityVerified: i !== 4, createdAt: new Date(Date.now() - 240 * DAY).toISOString(), displayName: p.name, handle: p.handle },
    wallet: { balance: 4_250 - i * 400, escrowBalance: i === 0 ? 92_000 : i === 2 ? 11_399 : 0, totalEarned: 102_000 - i * 9_000, totalSpent: 3_121 + i * 500, totalFlakeFees: i === 3 ? 600 : 0 },
    transactions: series(12, (back, k) => ({
      id: `tx-${userId}-${k}`, createdAt: stamp(back * 24), type: k % 3 === 0 ? 'HostEscrowRelease' : 'GuestCheckoutEarning',
      amount: k % 3 === 0 ? 4_000 : 2_000, balanceAfter: 4_250 - i * 400, eventTitle: titles[k % titles.length],
      description: k % 3 === 0 ? `Escrow released for ${titles[k % titles.length]}` : `Guest checkout ticket sale for ${titles[k % titles.length]}`,
    })),
    eventsHosted: titles.map((t, k) => ({ id: `ev-${userId}-${k}`, title: t, status: k === 0 ? 'selling' : 'ended', dateTime: stamp(-24 * (k + 2)), fundingGoalCents: 0, applicationCount: 12 + k * 7, acceptedCount: 10 + k * 7 })),
    applications: [{ id: `ap-${userId}`, eventTitle: 'Her party', status: 'paid', partyCreditsCommitted: 2_500, partyCreditsRefunded: false, attendanceStatus: 'attended', createdAt: stamp(96) }],
    withdrawals: [{ id: `wd-${userId}`, amountCents: 40_000, feeCents: 125, netAmountCents: 39_875, status: 'completed', createdAt: stamp(200) }],
  };
};

/** PEOPLE › HOSTS: the same five people as hosts, the ledger numbers in cents. */
const WALLETS = {
  total: 4,
  totals: { balanceCents: 1_284_950, escrowCents: 387_399, walletsWithMoney: 231, wallets: 4213 },
  wallets: PEOPLE.filter((_, i) => i !== 4).map((p, i) => ({
    userId: p.userId, email: p.email, displayName: p.name, handle: p.handle, isTestAccount: false,
    balance: 42_500 - i * 4_000, escrow: i === 0 ? 184_000 : i === 2 ? 11_399 : 0,
    totalEarned: 1_240_000 - i * 210_000, totalSpent: 18_400 - i * 2_000,
    transactionCount: 148 - i * 30, lastActivityAt: iso(i * 3 + 1),
  })),
};

const HOSTS = {
  total: 212,
  hosts: PEOPLE.map((p, i) => ({
    id: p.userId, email: p.email, displayName: p.name, handle: p.handle, avatarUrl: null, role: i === 2 ? 'admin' : null,
    identityVerified: i !== 4, payoutsReady: i !== 3, isTestAccount: false,
    eventsHosted: 14 - i * 2, eventsLive: i < 2 ? 1 : 0, eventsEnded: 11 - i * 2, ticketsSold: 320 - i * 60,
    grossCents: 1_240_000 - i * 210_000, walletBalance: 42_500 - i * 4_000, escrowBalance: i === 0 ? 184_000 : i === 2 ? 11_399 : 0,
    heldByLedgerCents: i === 0 ? 184_000 : i === 2 ? 11_399 : 0,
    instantEscrow: i === 1, escrowReleaseHours: i === 3 ? 2 : null,
    firstEventAt: new Date(Date.now() - (400 - i * 30) * DAY).toISOString(), lastEventAt: new Date(Date.now() - (2 + i * 9) * DAY).toISOString(),
    createdAt: new Date(Date.now() - (420 - i * 30) * DAY).toISOString(),
  })),
};
const hostStats = (days) => ({
  days, hosts: 212, hostingNow: 38, newHosts: Math.round(days / 3), escrowHeldCents: 457_900, ticketCentsInRange: days * 31_400,
  instantEscrowHosts: 4, customDelayHosts: 2, liveEvents: 61,
  sources: {
    hosts: "users WHERE EXISTS (events WHERE host.user_id = users.id AND status <> 'draft')", hostingNow: 'hosts with an event at selling, funding, greenlit or processing_greenlight',
    newHosts: "MIN(events.created_at) per host WHERE status <> 'draft', within the range", escrowHeld: 'SUM(wallets.escrow_balance) over hosts',
    ticketMoney: 'wallet_transactions WHERE type IN (HostEventEarning, CoHostEventEarning, GuestCheckoutEarning, ClaimEscrowIn, ClaimEscrowOut) AND created_at >= start',
    instant: 'users.instant_escrow', customDelay: 'users.escrow_release_hours IS NOT NULL',
  },
  series: series(days, (back, i) => ({ date: iso(back), newHosts: noise(i, 5) > 0.66 ? 1 : 0, ticketCents: Math.round(9_000 + noise(i, 6) * 60_000) })),
  topHosts: PEOPLE.map((p, i) => ({ label: p.name.toUpperCase(), value: 420_000 - i * 70_000, display: money(420_000 - i * 70_000), href: `/admin/hosts?person=${p.userId}` })),
  topHostsSource: 'the ticket credits above, grouped by the wallet they landed in',
});
const hostDetail = (userId) => {
  const h = HOSTS.hosts.find((x) => x.id === userId) || HOSTS.hosts[0];
  return {
    id: h.id, email: h.email, displayName: h.displayName, handle: h.handle, payoutsReady: h.payoutsReady, instantEscrow: h.instantEscrow, escrowReleaseHours: h.escrowReleaseHours,
    balance: h.walletBalance, escrowBalance: h.escrowBalance, totalEarned: h.grossCents, totalSpent: 12_000, withdrawnCents: 980_000, withdrawalCount: 9,
    eventsHosted: h.eventsHosted, eventsLive: h.eventsLive, eventsEnded: h.eventsEnded, eventsCanceled: 1, ticketsSold: h.ticketsSold, distinctGuests: Math.round(h.ticketsSold * 0.8),
    grossCents: h.grossCents, refundedCents: 15_000, releasedCents: h.grossCents - h.heldByLedgerCents - 15_000, heldByLedgerCents: h.heldByLedgerCents, otherEscrowCents: 0,
    firstEventAt: h.firstEventAt, lastEventAt: h.lastEventAt,
    held: h.heldByLedgerCents > 0 ? [
      { eventId: 'aaaa1111-0000-0000-0000-000000000001', title: 'Rooftop Supper Club', status: 'ended', dateTime: new Date(Date.now() - 1 * DAY).toISOString(), heldCents: h.heldByLedgerCents - 10_000, poolCents: h.heldByLedgerCents - 10_000, escrowReleaseAt: new Date(Date.now() + 0.4 * DAY).toISOString(), escrowReleasedAt: null,
        // A co-host added after half the tickets sold still gets their split of every ticket at release.
        shares: [
          { userId: h.id, handle: h.handle, name: h.displayName, isHost: true, splitLabel: 'HOST', holdsCents: h.heldByLedgerCents - 10_000, shareCents: Math.round((h.heldByLedgerCents - 10_000) * 0.7) },
          { userId: '22222222-2222-2222-2222-222222222222', handle: 'sam', name: 'Sam Ellis', isHost: false, splitLabel: '30%', holdsCents: 0, shareCents: (h.heldByLedgerCents - 10_000) - Math.round((h.heldByLedgerCents - 10_000) * 0.7) },
        ] },
      // One that has not happened yet: RELEASE NOW leaves it unticked.
      { eventId: 'aaaa1111-0000-0000-0000-000000000002', title: 'Springboard Diving Club Night', status: 'selling', dateTime: new Date(Date.now() + 27 * DAY).toISOString(), heldCents: 10_000, escrowReleaseAt: null, escrowReleasedAt: null },
    ] : [],
    recentReleases: [{ id: 'r1', createdAt: new Date(Date.now() - 12 * DAY).toISOString(), amount: 96_400, type: 'HostEscrowRelease', eventTitle: 'Lakeside Long Table', description: 'Escrow released for Lakeside Long Table' }],
  };
};
const hostRelease = (userId) => {
  const h = HOSTS.hosts.find((x) => x.id === userId) || HOSTS.hosts[0];
  const held = h.heldByLedgerCents;
  return {
    verdict: held > 0 ? 'delivered' : 'nothing_held', summary: held > 0 ? `${money(held)} delivered to the balance. It can be cashed out now.` : 'Nothing held. Nothing moved.',
    heldBeforeCents: held, movedCents: held, balanceBefore: h.walletBalance, balanceAfter: h.walletBalance + held, escrowBefore: h.escrowBalance, escrowAfter: h.escrowBalance - held,
    eventsReleased: held > 0 ? 1 : 0, ranAt: new Date().toISOString(),
    steps: held > 0 ? [
      { key: 'wallet', label: 'FIND THE WALLET', status: 'done', detail: `Balance ${money(h.walletBalance)} · escrow ${money(h.escrowBalance)}.` },
      { key: 'ledger', label: 'READ THE LEDGER', status: 'done', detail: `1 event holding ${money(held)} by the ledger.`, cents: held },
      { key: 'release:aaaa1111-0000-0000-0000-000000000001', label: 'RELEASE · ROOFTOP SUPPER CLUB', status: 'done', detail: `${money(held)} moved into the balance (ended).`, cents: held },
      { key: 'check', label: 'CHECK THE WALLET', status: 'done', detail: `Balance ${money(h.walletBalance + held)} · escrow ${money(h.escrowBalance - held)}.`, cents: held },
    ] : [
      { key: 'wallet', label: 'FIND THE WALLET', status: 'done', detail: `Balance ${money(h.walletBalance)} · escrow ${money(h.escrowBalance)}.` },
      { key: 'ledger', label: 'READ THE LEDGER', status: 'skipped', detail: 'No event holds money for this wallet.' },
    ],
  };
};

const BRAND_REPORT = [
  { brandId: 'b1', brandName: 'Okanagan Spirits', logoUrl: null, patronDays: 6, dispatchedDays: 5, regions: 3, lastDayActive: iso(4), paidCents: 30_000, giftsCredited: 412, giftedCents: 206_000, giftsSpent: 151, spentCents: 75_500, opens: 980, clicks: 241, peopleReached: 862, clickRate: 0.246, spendRate: 0.367 },
  { brandId: 'b2', brandName: 'Mission Hill', logoUrl: null, patronDays: 3, dispatchedDays: 3, regions: 1, lastDayActive: iso(18), paidCents: 15_000, giftsCredited: 180, giftedCents: 90_000, giftsSpent: 44, spentCents: 22_000, opens: 410, clicks: 62, peopleReached: 388, clickRate: 0.151, spendRate: 0.244 },
  { brandId: 'b3', brandName: 'Quiet Co', logoUrl: null, patronDays: 1, dispatchedDays: 1, regions: 1, lastDayActive: iso(60), paidCents: 5_000, giftsCredited: 0, giftedCents: 0, giftsSpent: 0, spentCents: 0, opens: 0, clicks: 0, peopleReached: 0, clickRate: null, spendRate: null },
];

const REGION = '99999999-9999-9999-9999-999999999999';

const PATRONS = [
  { id: 'cccc1111-0000-0000-0000-000000000001', brandId: 'b1', brandName: 'Okanagan Spirits', regionId: REGION, regionName: 'Kelowna', regionSlug: 'kelowna', dayActive: iso(4), slotStatus: 'confirmed', guestGiftCents: 500, hostGiftCents: 2000, paidAmountCents: 5000, dispatchedAt: new Date(Date.now() - 4 * DAY).toISOString(), opens: 212, clicks: 58, giftsCredited: 94, giftsSpent: 31 },
  { id: 'cccc1111-0000-0000-0000-000000000002', brandId: 'b2', brandName: 'Mission Hill', regionId: REGION, regionName: 'Kelowna', regionSlug: 'kelowna', dayActive: iso(18), slotStatus: 'confirmed', guestGiftCents: 500, hostGiftCents: 2000, paidAmountCents: 5000, dispatchedAt: new Date(Date.now() - 18 * DAY).toISOString(), opens: 140, clicks: 19, giftsCredited: 61, giftsSpent: 12 },
  { id: 'cccc1111-0000-0000-0000-000000000003', brandId: 'b3', brandName: 'Quiet Co', regionId: REGION, regionName: 'Vernon', regionSlug: 'vernon', dayActive: iso(-6), slotStatus: 'confirmed', guestGiftCents: 500, hostGiftCents: 2000, paidAmountCents: 5000, dispatchedAt: null, opens: 0, clicks: 0, giftsCredited: 0, giftsSpent: 0 },
];

const BRANDS = [
  { id: 'b1', name: 'Okanagan Spirits', slug: 'okanagan-spirits', logoPath: 'brands/os.webp', logoUrl: null, promoLink: 'https://okanaganspirits.com/portal', defaultPromoCode: 'PORTAL10', defaultMessage: 'A round on us.', createdAt: new Date(Date.now() - 200 * DAY).toISOString(), patronDays: 6, lastDayActive: iso(4) },
  { id: 'b2', name: 'Mission Hill', slug: 'mission-hill', logoPath: 'brands/mh.webp', logoUrl: null, promoLink: 'https://missionhillwinery.com', defaultPromoCode: null, defaultMessage: null, createdAt: new Date(Date.now() - 160 * DAY).toISOString(), patronDays: 3, lastDayActive: iso(18) },
  { id: 'b3', name: 'Quiet Co', slug: 'quiet-co', logoPath: null, logoUrl: null, promoLink: null, defaultPromoCode: null, defaultMessage: null, createdAt: new Date(Date.now() - 90 * DAY).toISOString(), patronDays: 1, lastDayActive: iso(60) },
];

const CURATOR_EVENTS = {
  events: [
    { id: 'aaaa1111-0000-0000-0000-000000000001', title: 'Rooftop Supper Club', status: 'selling', dateTime: new Date(Date.now() + 9 * DAY).toISOString(), category: 'dining', regionSlug: 'kelowna', regionName: 'Kelowna', isFeatured: true, heatScore: 0.82, hostName: 'Maya Okonkwo', posterUrl: null, inviteCode: 'ROOF01', locationCity: 'Kelowna, BC', locationLatitude: 49.888, locationLongitude: -119.496, isIngested: false, miles: 3.2, priceCents: 6500, applicationCount: 31, acceptedCount: 22, maxAttendees: 30 },
    { id: 'aaaa1111-0000-0000-0000-000000000002', title: 'Vinyl + Vermouth', status: 'funding', dateTime: new Date(Date.now() + 16 * DAY).toISOString(), category: 'music', regionSlug: 'penticton', regionName: 'Penticton', isFeatured: false, heatScore: 0.41, hostName: 'Sam Ellis', posterUrl: null, inviteCode: 'VINY02', locationCity: 'Penticton, BC', locationLatitude: 49.5, locationLongitude: -119.594, isIngested: false, miles: 38.4, priceCents: 4500, applicationCount: 18, acceptedCount: 14, maxAttendees: 0 },
    { id: 'aaaa1111-0000-0000-0000-000000000003', title: 'Night Market Crawl', status: 'selling', dateTime: new Date(Date.now() + 21 * DAY).toISOString(), category: 'adventure', regionSlug: 'calgary', regionName: 'Calgary', isFeatured: false, heatScore: 0.12, hostName: 'Priya Nair', posterUrl: null, inviteCode: 'NGHT03', locationCity: 'Calgary, AB', locationLatitude: 51.045, locationLongitude: -114.057, isIngested: true, miles: null, priceCents: 0, applicationCount: 0, acceptedCount: 0, maxAttendees: 0 },
  ],
};

const WALL = {
  picks: [
    { id: 'w1', eventId: 'aaaa1111-0000-0000-0000-000000000001', category: 'dining', position: 1, pickedAt: iso(6), eventTitle: 'Rooftop Supper Club', hostName: 'Maya Okonkwo', eventStatus: 'selling', eventDateTime: new Date(Date.now() + 9 * DAY).toISOString(), pickedByName: 'Dana Reyes', clicks: 412 },
    { id: 'w2', eventId: 'aaaa1111-0000-0000-0000-000000000002', category: 'music', position: 2, pickedAt: iso(2), eventTitle: 'Vinyl + Vermouth', hostName: 'Sam Ellis', eventStatus: 'funding', eventDateTime: new Date(Date.now() + 16 * DAY).toISOString(), pickedByName: 'Marcus Tan', clicks: 86 },
  ],
};

const COUNTS = { ingestionQueue: 12, openDisputes: 2, pendingWithdrawals: 3, listedProClaims: 1, newsletterDrafts: 1, engineAwaitingReview: 2, pendingInvites: 1, criticalActivity24h: 1, failingSources: 2, eventClaims: 1 };

/** url → fixture. The first match wins, so put the specific ones first. */

// APP NOTICES — the sheets, the pushes, the audience count and the link picker's catalog, so
// /admin-preview/notices?data=demo drives the whole composer (design_handoff_app_notices).
const NOTICE_IMG = 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1200&q=70';
const SHEETS = [
  { id: 'n1', eyebrow: 'ANNOUNCEMENT · PENTICTON', headline: 'The City Portal is open in Penticton.', body: 'Find out what is on this week, who is hosting, and where to show up.', heroKind: 'plate', heroValue: null, accent: '#22ECFB', primaryLabel: 'ENTER THE SQUARE', primaryHref: '/square', secondaryLabel: 'NOT NOW', audience: { scopes: [], clubIds: [], userIds: [], newWithinDays: null, labels: {} }, cities: [{ slug: 'penticton', label: 'Penticton, British Columbia, Canada', lat: 49.4991, lng: -119.5937, radiusMiles: 50 }], audienceSummary: 'EVERYONE', publishStart: iso(6), publishEnd: iso(-8), isActive: true, createdAt: iso(7), seenCount: 2318, actedCount: 412 },
  { id: 'n2', eyebrow: 'FOR HOSTS', headline: 'Payouts land in two days now.', body: 'Cash out from the wallet and it is in your bank by Thursday.', heroKind: 'image', heroValue: NOTICE_IMG, accent: '#B5B1FF', primaryLabel: 'OPEN THE WALLET', primaryHref: '/wallet', secondaryLabel: 'NOT NOW', audience: { scopes: ['hosts'], clubIds: [], userIds: [], newWithinDays: null, labels: {} }, cities: [], audienceSummary: 'HOSTS', publishStart: null, publishEnd: null, isActive: true, createdAt: iso(3), seenCount: 588, actedCount: 201 },
  { id: 'n3', eyebrow: 'NEW', headline: 'Pro pages are here.', body: 'A page for your business, bookable from Portal.', heroKind: 'color', heroValue: '#FCAA6D', accent: '#FAE1B4', primaryLabel: 'SEE PRO PAGES', primaryHref: '/pro', secondaryLabel: 'NOT NOW', audience: { scopes: ['pros'], clubIds: [], userIds: [], newWithinDays: null, labels: {} }, cities: [], audienceSummary: 'PROS', publishStart: iso(20), publishEnd: iso(12), isActive: false, createdAt: iso(21), seenCount: 81, actedCount: 40 },
];
const PUSHES = [
  { id: 'q1', title: 'Saturday is almost sold out.', body: 'Dinner at the long table has nine seats left. Doors at eight.', imagePath: null, imageUrl: NOTICE_IMG, linkPath: '/e/kelowna/rooftop-supper-club', audience: { scopes: ['guests'], clubIds: [], userIds: [], newWithinDays: null, labels: {} }, cities: [], audienceSummary: 'GUESTS', status: 'sent', recipientCount: 4102, sentAt: iso(2), sentByName: 'Brenden', createdAt: iso(2) },
  { id: 'q2', title: 'The market is back on Saturdays.', body: 'Twelve stalls and a coffee cart.', imagePath: null, imageUrl: null, linkPath: '/square/penticton', audience: { scopes: [], clubIds: [], userIds: [], newWithinDays: null, labels: {} }, cities: [], audienceSummary: 'EVERYONE', status: 'draft', recipientCount: 0, sentAt: null, sentByName: null, createdAt: iso(0) },
];
const LINKS = [
  { kind: 'EVENT', path: '/e/kelowna/rooftop-supper-club', label: 'Rooftop supper club', sub: 'SAT OCT 24', img: NOTICE_IMG },
  { kind: 'EVENT', path: '/e/penticton/colours-in-the-park', label: 'Colours in the park', sub: 'SUN OCT 25', img: null },
  { kind: 'CLUB', path: '/clubs/da-gang', label: 'Da Gang', sub: null, img: null },
  { kind: 'PRO', path: '/pro/skaha-surf', label: 'Skaha Surf', sub: null, img: null },
  { kind: 'PROFILE', path: '/maya', label: 'Maya Reyes', sub: '@maya', img: null },
  { kind: 'CITY', path: '/square/okanagan', label: 'City Portal · Okanagan', sub: null, img: null },
  { kind: 'CITY', path: '/events/in/okanagan', label: 'Events in Okanagan', sub: null, img: null },
];
const resolveDemoLink = (path) => {
  const hit = LINKS.find((l) => l.path === path);
  if (hit) return { kind: hit.kind, label: hit.label, note: hit.sub || path, ok: true, warn: false, path, image: hit.img };
  const seg = (path || '').split('/')[1] || '';
  if (/^\/clubs\//.test(path)) return { kind: 'CHECK', label: 'No club at this link', note: `No club matches ${path}.`, ok: false, warn: true, path };
  if (/^\/e\//.test(path)) return { kind: 'CHECK', label: 'Nothing lives at this link', note: `No event matches ${path}.`, ok: false, warn: true, path };
  if (/^\/pro\//.test(path)) return { kind: 'CHECK', label: 'No Pro page at this link', note: `No Pro page matches ${path}.`, ok: false, warn: true, path };
  return { kind: 'CHECK', label: `No one is @${seg}`, note: 'Single words open a profile. Check the handle.', ok: false, warn: true, path };
};

const ROUTES = [
  [/\/app-notices\/admin\/audience\/count/, () => ({ success: true, data: { count: 2744 } })],
  [/\/app-notices\/admin\/links\/resolve/, (q) => ({ success: true, data: resolveDemoLink(q.get('path') || '') })],
  [/\/app-notices\/admin\/links/, (q) => { const t = (q.get('q') || '').toLowerCase(); return { success: true, data: LINKS.filter((l) => !t || l.label.toLowerCase().includes(t) || l.path.includes(t)) }; }],
  [/\/app-notices\/admin\/clubs/, () => ({ success: true, data: [{ id: 'c1', name: 'Da Gang' }, { id: 'c2', name: 'Naramata Run Club' }] })],
  [/\/app-notices\/admin\/people/, () => ({ success: true, data: [{ id: 'p1', email: 'maya@x.com', displayName: 'Maya Reyes', handle: 'maya' }] })],
  [/\/app-notices\/admin\/pushes\/[^/]+\/send/, () => ({ success: true, data: { ...PUSHES[1], status: 'sent', recipientCount: 2744, sentAt: iso(0) } })],
  [/\/app-notices\/admin\/pushes/, () => ({ success: true, data: PUSHES })],
  [/\/app-notices\/admin(\?|$)/, () => ({ success: true, data: SHEETS })],
  [/\/app-notices\/admin\/[0-9a-z-]+$/i, () => ({ success: true, data: SHEETS[0] })],
  [/\/staff\/counts/, () => COUNTS],
  [/\/partypatrons\/reports\/brands/, () => ({ success: true, data: BRAND_REPORT })],
  [/\/partypatrons(\?|$)/, () => ({ success: true, data: PATRONS })],
  [/\/brands(\?|$)/, () => ({ success: true, data: BRANDS })],
  [/\/curator\/events/, () => ({ success: true, data: CURATOR_EVENTS })],
  [/\/curator\/wall/, () => ({ success: true, data: WALL })],
  // FEATURED only asks for its wall once the AREA has resolved to a region, so the
  // harness has to answer that too or the page is honestly empty.
  [/\/regions\/for-coords/, () => ({ success: true, data: { id: REGION, name: 'Kelowna', slug: 'kelowna' } })],
  [/\/admin\/stats\/breakdowns/, () => BREAKDOWNS],
  [/\/admin\/stats\/revenue/, (q) => revenue(Number(q.get('days')) || 30)],
  [/\/admin\/stats\/signups/, (q) => signups(Number(q.get('days')) || 30)],
  [/\/admin\/stats\/escrow/, () => ESCROW],
  [/\/admin\/stats\/fees/, () => FEES],
  [/\/admin\/stats$/, () => STATS],
  // WALLET (Oct 28 2026): the person picker, a transfer and an adjust answer like the server would.
  [/\/admin\/wallets\/people/, (q) => {
    const t = (q.get('q') || '').replace(/^@/, '').toLowerCase();
    return { people: PEOPLE.filter((p) => p.userId !== q.get('exclude') && (p.name.toLowerCase().includes(t) || p.email.includes(t) || p.handle.includes(t)))
      .map((p, i) => ({ id: p.userId, email: p.email, displayName: p.name, handle: p.handle, avatarUrl: null, role: null, isTestAccount: false, balance: 4_250 - i * 400, escrow: 0 })) };
  }],
  [/\/admin\/users\/([0-9a-f-]+)\/wallet-transfer/i, (q, m) => ({ amountCents: 1000, fromUserId: m[1], fromBalanceBefore: 4_250, fromBalanceAfter: 3_250, toUserId: PEOPLE[1].userId, toBalanceBefore: 3_850, toBalanceAfter: 4_850, transactionIds: [] })],
  [/\/admin\/users\/([0-9a-f-]+)\/wallet-adjustment/i, () => ({ transactionId: 'demo', amount: 1000, balanceBefore: 4_250, balanceAfter: 5_250 })],
  [/\/admin\/users\/([0-9a-f-]+)$/i, (q, m) => ({ success: true, data: userDetail(m[1]) })],
  [/\/admin\/users/, () => USERS],
  [/\/admin\/wallets/, (q) => ({ ...WALLETS, wallets: WALLETS.wallets.filter((w) => { const t = (q.get('search') || '').toLowerCase(); return !t || w.email.includes(t) || w.displayName.toLowerCase().includes(t) || w.handle.includes(t); }) })],
  [/\/admin\/hosts\/stats/, (q) => hostStats(Number(q.get('days')) || 30)],
  [/\/admin\/hosts\/([0-9a-f-]+)\/release-escrow/i, (q, m) => hostRelease(m[1])],
  [/\/admin\/hosts\/([0-9a-f-]+)\/escrow-settings/i, () => ({ instantEscrow: true, escrowReleaseHours: null })],
  [/\/admin\/hosts\/([0-9a-f-]+)$/i, (q, m) => hostDetail(m[1])],
  [/\/admin\/hosts/, () => HOSTS],
  [/\/analytics\/overview/, (q) => ({ success: true, data: overview(Number(q.get('days')) || 30) })],
  [/\/analytics\/people\/([0-9a-f-]+)\/day/i, (q, m) => ({ success: true, data: personDay(m[1], q.get('date') || iso(1)) })],
  [/\/analytics\/people\/([0-9a-f-]+)/i, (q, m) => ({ success: true, data: person(m[1], Number(q.get('days')) || 14) })],
  [/\/analytics\/people/, (q) => ({ success: true, data: people(Number(q.get('days')) || 30) })],
  [/\/analytics\/day/, (q) => ({ success: true, data: day(q.get('date') || iso(1)) })],
];

let installed = false;

/** Answer the panel's reads from the fixtures above. Idempotent; dev-only. */
export function installPreviewFixtures() {
  if (installed || process.env.NODE_ENV === 'production') return;
  installed = true;
  api.interceptors.request.use((config) => {
    const url = config.url || '';
    const [, build, match] = ROUTES.map(([re, fn]) => {
      const m = re.exec(url);
      return m ? [true, fn, m] : null;
    }).find(Boolean) || [];
    if (!build) return config;
    // The query may ride the URL string or `params`; a fixture reads both the same way.
    const query = new URLSearchParams(url.split('?')[1] || '');
    Object.entries(config.params || {}).filter(([, v]) => v != null).forEach(([k, v]) => query.set(k, String(v)));
    const data = build(query, match);
    // Short-circuit by swapping the adapter for this one request.
    return { ...config, adapter: async () => ({ data, status: 200, statusText: 'OK', headers: {}, config }) };
  });
}
